import api from "./api-utils.js";

/**
 * @typedef {Object} MappedClassifications
 * @property {number} AlarmMessageLookupId - PK for the classification mapping.
 * @property {string} Message - The alarm message text.
 * @property {string} Vendor - The vendor the mapping applies to.
 * @property {boolean|null|undefined} IsMech - The classification for the downtime - true, false, or unknown (null/undefined).
 */

/**
 * @typedef {Object} UnmappedClassifications
 * @property {string} Message - The alarm message text.
 * @property {string} Vendor - The vendor the mapping applies to.
 * @property {number} DowntimeEventCount - How many downtime events can be attributed to the combination of vendor and message.
 * @property {number} DowntimeHoursCount - The total duration of downtime events attributed to this alarm message in the search range, in hours.
 */

/**
 * Fetches unmapped downtime classifications for the current tenant within the specified date range.
 * @param {string} startDate - The start date for the search range as an ISO string (`yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`).
 * @param {string} endDate - The end date for the search range as an ISO string (`yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`).
 * @param {string} vendor - The vendor to retrieve the unmapped classifications for.
 * @returns {Promise<UnmappedClassifications[]>}
 */
export async function getUnmappedDowntimeClassifications(
  startDate,
  endDate,
  vendor
) {
  const { data } = await api.get("/api/alarms/get-unmapped-alarm-messages", {
    params: { startSearch: startDate, endSearch: endDate, vendor },
  });
  return data?.AlarmMessages;
}

/**
 * Fetches mapped downtime classifications for the current tenant. There is no date range constraint, all mappings are retrieved.
 * @returns {Promise<MappedClassifications[]>}
 */
export async function getMappedDowntimeClassifications() {
  const { data } = await api.get("/api/alarms/get-mapped-alarm-messages");
  return data?.AlarmMessageLookups;
}

/**
 * @typedef {Object} ClassificationToSave
 * @property {number} AlarmMessageLookupId - The id of the classification to save. If this is a new classification, this id should be set to 0.
 * @property {string} Message -  The alarm message text.
 * @property {string} Vendor - The vendor the mapping applies to.
 *  @property {boolean|null|undefined} IsMech - The classification for the downtime - true, false, or unknown (null/undefined).
 */

/**
 * Saves downtime classifications for the current tenant.
 * @param {ClassificationToSave[]} classifications
 * @returns {Promise<void>}
 */
export async function saveDowntimeClassification(classifications) {
  await api.post("/api/alarms/save-alarm-message-map", {
    AlarmMessageLookups: classifications,
  });
}
